import { errorMessage } from './shared/lib/errors';
import { nonEmptyString } from './shared/lib/format';
import { logger } from './shared/lib/logger';
import type { QuickActionData, DocketPreviewEvent } from './shared/components/QuickActionModal';
import type { ProofGraph, MatterResponse } from './shared/types';
import { ConfirmationHost } from './shared/components/ConfirmationHost';
import React, { lazy, Suspense, useState, useEffect } from 'react';
import type { AuthorityResearchTarget, ContextAISourceRef, ContextAIUIContext, Fact, FactLink, Evidence, Preferences, ClioContact } from './shared/types';
import { ConfigController } from './shared/lib/controller';
import { StageBoard } from './shared/components/StageBoard';

import { DbLoadingGraph } from './components/DbLoadingGraph';
import { ModalDialog } from './shared/components/ModalDialog';

import { LoadingScreen } from './shared/components/LoadingScreen';
import { ErrorScreen } from './shared/components/ErrorScreen';
import { AuthScreen } from './shared/components/AuthScreen';
import { API_URL, apiFetch, jsonInit } from './shared/lib/api';
import { parseJurisdiction } from './shared/lib/jurisdiction';
import { launchProCheckout } from './shared/lib/billing';
import { mergeClioContacts, resolveMatterContactOptions, upsertClioContact } from './shared/lib/contacts';
import { setPrinciplesForTarget } from './shared/lib/authorityInsights';
import { migrateProofGraph } from './shared/lib/proofGraph';
import { useToast } from './shared/hooks/useToast';
import { useModalConfig } from './shared/hooks/useModalConfig';
import { AppSidebar, type TabId } from './shared/components/layout/AppSidebar';
import { AppTopbar } from './shared/components/layout/AppTopbar';
import { usePartyForm } from './shared/hooks/usePartyForm';
import { usePreferencesDraft } from './shared/hooks/usePreferencesDraft';
import { useMatterData } from './shared/hooks/useMatterData';
import { useAIUsage } from './shared/hooks/useAIUsage';
import { useProofGraphActions } from './shared/hooks/useProofGraphActions';
import { PreferencesSaveBar } from './shared/components/preferences/PreferencesSaveBar';
import type { UserPrefsSubTab } from './shared/components/UserPreferencesOverlay';

// Keep the app shell and default dashboard in the entry bundle. Everything
// below is fetched only when the user activates the corresponding tab or
// interaction, so large feature dependencies (notably docx and graph engines)
// do not delay the first useful render.
const ConversationThread = lazy(() => import('./shared/components/ConversationThread').then((module) => ({ default: module.ConversationThread })));
const OnboardingWizard = lazy(() => import('./shared/components/OnboardingWizard').then((module) => ({ default: module.OnboardingWizard })));
const ProofGraphVisualizer = lazy(() => import('./components/Graph/ProofGraphVisualizer').then((module) => ({ default: module.ProofGraphVisualizer })));
const MotionsHub = lazy(() => import('./shared/components/motions/MotionsHub').then((module) => ({ default: module.MotionsHub })));
const NegotiationPane = lazy(() => import('./components/NegotiationPane').then((module) => ({ default: module.NegotiationPane })));
const CreateDocuments = lazy(() => import('./components/CreateDocuments').then((module) => ({ default: module.CreateDocuments })));
const ReportsTab = lazy(() => import('./components/ReportsTab').then((module) => ({ default: module.ReportsTab })));
const AIIntakeHub = lazy(() => import('./shared/components/ai-intake/AIIntakeHub').then((module) => ({ default: module.AIIntakeHub })));
const FactsLens = lazy(() => import('./shared/components/FactsLens').then((module) => ({ default: module.FactsLens })));
const CaseContextLens = lazy(() => import('./shared/components/case-context/CaseContextLens').then((module) => ({ default: module.CaseContextLens })));
const AuthoritiesLens = lazy(() => import('./shared/components/authorities/AuthoritiesLens').then((module) => ({ default: module.AuthoritiesLens })));
const FactMetadataPanel = lazy(() => import('./shared/components/FactsLens').then((module) => ({ default: module.FactMetadataPanel })));
const MapperTab = lazy(() => import('./shared/components/mapper/MapperTab').then((module) => ({ default: module.MapperTab })));
const PartiesTab = lazy(() => import('./shared/components/parties/PartiesTab').then((module) => ({ default: module.PartiesTab })));
const PartyFormModal = lazy(() => import('./shared/components/parties/PartyFormModal').then((module) => ({ default: module.PartyFormModal })));
const PreferencesPanel = lazy(() => import('./shared/components/preferences/PreferencesPanel').then((module) => ({ default: module.PreferencesPanel })));
const ClearDataConfirmModal = lazy(() => import('./shared/components/preferences/ClearDataConfirmModal').then((module) => ({ default: module.ClearDataConfirmModal })));
const UnsavedChangesModal = lazy(() => import('./shared/components/UnsavedChangesModal').then((module) => ({ default: module.UnsavedChangesModal })));
const QuickActionModal = lazy(() => import('./shared/components/QuickActionModal').then((module) => ({ default: module.QuickActionModal })));
const UserPreferencesOverlay = lazy(() => import('./shared/components/UserPreferencesOverlay').then((module) => ({ default: module.UserPreferencesOverlay })));
const ContactSearchModal = lazy(() => import('./shared/components/ContactSearchModal').then((module) => ({ default: module.ContactSearchModal })));
const ExpertsLens = lazy(() => import('./shared/components/experts/ExpertsLens').then((module) => ({ default: module.ExpertsLens })));
const DocketLens = lazy(() => import('./shared/components/docket/DocketLens').then((module) => ({ default: module.DocketLens })));
const MatterDetailsPanel = lazy(() => import('./shared/components/matter-details/MatterDetailsPanel').then((module) => ({ default: module.MatterDetailsPanel })));
const ContextAssistantFAB = lazy(() => import('./shared/components/context-assistant/ContextAssistantFAB').then((module) => ({ default: module.ContextAssistantFAB })));

function ViewChunkFallback(): React.JSX.Element {
  return (
    <div className="view show lazy-view-loading" role="status" aria-live="polite">
      <span className="lazy-view-spinner" aria-hidden="true" />
      <span>Loading workspace…</span>
    </div>
  );
}

// =============================================================================
// APP.TSX — ROOT SHELL
// This file wires together auth/routing/global data-fetching and composes the
// per-tab views below. It is being incrementally decomposed into small
// reusable components and hooks (see the refactor plan); this banner is
// updated as each piece lands so it stays an accurate index.
//
// Concern -> file map:
//   Pool id generation / legacy graph migration -> shared/lib/proofGraph.ts
//   Toast notifications                          -> shared/hooks/useToast.ts
//   Generic alert/confirm/prompt modal            -> shared/hooks/useModalConfig.ts
//   Left nav sidebar                              -> shared/components/layout/AppSidebar.tsx
//   Topbar (breadcrumb, archetype badge, avatar)  -> shared/components/layout/AppTopbar.tsx
//   Litigation Mapper tab                         -> shared/components/mapper/MapperTab.tsx
//   Parties & Counsel tab / party form             -> shared/components/parties/PartiesTab.tsx, PartyFormModal.tsx, shared/hooks/usePartyForm.ts
//   Preferences tab / draft state / save-bar       -> shared/components/preferences/PreferencesPanel.tsx, shared/hooks/usePreferencesDraft.ts
//   Quick Action / Court Rules modal               -> shared/components/QuickActionModal.tsx
//   Global user preferences slide-over             -> shared/components/UserPreferencesOverlay.tsx
//   Contact search modal                           -> shared/components/ContactSearchModal.tsx
//   Matter/global data fetching (auth, polling,
//     lazy-loads)                                   -> shared/hooks/useMatterData.ts
//   Proof-graph CRUD (facts/evidence/damages/
//     motions/claims/tasks/templates)               -> shared/hooks/useProofGraphActions.ts
// =============================================================================

function App(): React.JSX.Element {
  const { modalConfig, showAlert, showConfirm, showPrompt, close } = useModalConfig();

  // Navigation and UI state
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [authorityFocus, setAuthorityFocus] = useState<AuthorityResearchTarget | null>(null);
  const [openThreadClaimId, setOpenThreadClaimId] = useState<string | null>(null);
  const [openFactMetadataId, setOpenFactMetadataId] = useState<string | null>(null);
  const [factMetadataInitialSection, setFactMetadataInitialSection] = useState('basic');
  const [mapperReferenceFocus, setMapperReferenceFocus] = useState<{ type: 'claim' | 'element'; id: string } | null>(null);
  const [authorityReferenceId, setAuthorityReferenceId] = useState<string | null>(null);
  const [documentReferenceId, setDocumentReferenceId] = useState<string | null>(null);

  // Global User Preferences Overlay
  const [showUserPrefs, setShowUserPrefs] = useState(false);
  const [userPrefsSubTab, setUserPrefsSubTab] = useState<UserPrefsSubTab>('profile');

  // Success Notification Toast state
  const { toastMessage, showToast, notify } = useToast();

  // Raw `preferences` state stays here (not inside useMatterData) so it can
  // be passed to usePreferencesDraft without a circular dependency between
  // the two hooks; useMatterData's fetchGlobals effect reports the fetched
  // preferences back via the onPreferencesLoaded callback below.
  const [preferences, setPreferences] = useState<Preferences | null>(null);

  // Preferences draft/edit state, dirty-tracking, save/discard, the
  // nav-away guard, and the danger-zone clear-data flow now live in
  // usePreferencesDraft() — see shared/hooks/usePreferencesDraft.ts.
  const prefsDraft = usePreferencesDraft({ preferences, setPreferences, activeTab, setActiveTab, notify, showAlert });
  const tryNavigate = prefsDraft.tryNavigate;
  const isPreferencesDirty = prefsDraft.isDirty;
  const openAuthorities = (target: AuthorityResearchTarget): void => {
    setAuthorityReferenceId(null);
    setAuthorityFocus(target);
    tryNavigate('authorities');
  };

  // Matter/global data-fetching (auth, matter resolution, polling, and the
  // documents/threads/damages lazy-loads) now lives in useMatterData() — see
  // shared/hooks/useMatterData.ts. Destructured back into the same local
  // names App.tsx already used so every downstream reference is unchanged.
  const {
    matterId, setMatterId, mattersList, showAllMatters, mattersListLoading, moreMattersLoading, changeMatterScope,
    loading, dbLoading, dbLoadingStates, error,
    authenticated, showWizard, setShowWizard,
    isPremiumUnlocked, setIsPremiumUnlocked,
    clioMatter, clioTasks, setClioTasks, clioDocuments,
    clioActivities, setClioActivities, clioCalendarEvents, setClioCalendarEvents,
    proofGraph, setProofGraph,
    templates, setTemplates,
    damages, setDamages,
    contacts, setContacts, accountUsers, matterContacts, setMatterContacts,
  } = useMatterData({ activeTab, onPreferencesLoaded: prefsDraft.updateSavedPreferences });
  const aiUsage = useAIUsage(authenticated);

  // Proof-graph CRUD (saveGraph + fact/evidence/damages/motions/claims/
  // tasks/templates handlers) now lives in useProofGraphActions() — see
  // shared/hooks/useProofGraphActions.ts. Must be called unconditionally,
  // before any early returns below, since it's a hook (Rules of Hooks).
  const graphActions = useProofGraphActions({
    matterId, proofGraph, setProofGraph,
    templates, setTemplates,
    damages, setDamages,
    clioTasks, setClioTasks,
    clioCalendarEvents, setClioCalendarEvents,
    showAlert, showConfirm,
  });
  const partyForm = usePartyForm({
    proofGraph,
    saveGraph: graphActions.saveGraph,
    setContacts,
    showConfirm,
    responsibleAttorney: clioMatter?.responsible_attorney,
  });

  // Mapper-tab-local UI state (lens toggle, add-claim/add-fact/link-fact
  // modals) now lives inside MapperTab itself — see
  // shared/components/mapper/MapperTab.tsx.

  // Quick action modal state
  const [activeModal, setActiveModal] = useState<'task' | 'calendar' | 'note' | 'time' | 'rules' | null>(null);
  const [modalData, setModalData] = useState<QuickActionData>({});

  // Court Rules triggers
  const [jurisdictions, setJurisdictions] = useState<{ id: string; name: string }[]>([]);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('');
  const [ruleTriggers, setRuleTriggers] = useState<{ id: string; name: string }[]>([]);
  const [selectedTrigger, setSelectedTrigger] = useState('');
  const [docketPreview, setDocketPreview] = useState<DocketPreviewEvent[]>([]);

  // Contact Search Modal State
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchModalOnSelect, setSearchModalOnSelect] = useState<((contact: ClioContact) => void) | null>(null);
  const [searchModalContext, setSearchModalContext] = useState<{ partyRole?: string; fieldType?: 'clio_contact_id' | 'counsel_contact_id' | 'paralegal_contact_id' | 'related_contact' }>({});
  const [searchModalLoading, setSearchModalLoading] = useState(false);

  // Party add/edit form state + handlers now live in usePartyForm() — see
  // shared/hooks/usePartyForm.ts (instantiated below, once saveGraph exists).

  // Load jurisdictions for Court Rules
  useEffect(() => {
    if (activeModal === 'rules') {
      apiFetch(`/api/v1/matters/` + matterId + '/rules/jurisdictions')
        .then(res => res.json<{ id: string; name: string }[]>())
        .then(data => setJurisdictions((data ?? [])))
        .catch(err => logger.error('Failed to load jurisdictions', err));
    }
  }, [activeModal, matterId]);

  // Load triggers for selected jurisdiction
  const handleJurisdictionChange = (jurisdictionId: string): void => {
    setSelectedJurisdiction(jurisdictionId);
    apiFetch(`/api/v1/matters/${matterId}/rules/triggers?jurisdiction_id=${jurisdictionId}`)
      .then(res => res.json<{ id: string; name: string }[]>())
      .then(data => setRuleTriggers((data ?? [])))
      .catch(err => logger.error('Failed to load triggers', err));
  };

  // Preview Court Rules deadlines
  const handleTriggerChange = (triggerId: string): void => {
    setSelectedTrigger(triggerId);
    apiFetch(`/api/v1/matters/${matterId}/rules/preview?trigger_id=${triggerId}`)
      .then(res => res.json<DocketPreviewEvent[]>())
      .then(data => setDocketPreview((data ?? [])))
      .catch(err => logger.error('Failed to load preview', err));
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen message={error} />;
  }

  const isAuth = authenticated === true;

  if (!isAuth) {
    return <AuthScreen />;
  }

  // Resolve template structure and proof graph scores via Config Controller
  const effectivePrefs = preferences ?? {
    id: 'default',
    type: 'prefs' as const,
    archetype: 'balanced-civil' as const,
    default_lens: 'ladder' as const,
    tier_labels: {},
    guidance_dismissed: { narrative: false, configuration: false },
    nav_order: ['stages', 'float', 'claims', 'actions', 'triggers', 'aud']
  };
  const effectiveProofGraph: ProofGraph = proofGraph ?? {
    matter_id: matterId,
    active_stage_id: 'presuit',
    floating_states: {},
    parties: [],
    claims: [],
    facts: [],
    evidence: [],
    motions: [],
    template_id: 'civil-ne'
  };

  const controller = new ConfigController(templates, effectivePrefs);
  const resolved = controller.resolve(effectiveProofGraph);

  // The flat, shared Fact/Evidence pools — the single source of truth.
  // Elements reference facts by id (element.fact_links); facts reference
  // evidence by id (fact.evidence_ids).
  const allFacts: Fact[] = (effectiveProofGraph.facts ?? []);
  const allEvidence: Evidence[] = (effectiveProofGraph.evidence ?? []);
  // Matter contacts are fetched independently and can be newer than the
  // firm-wide directory. Use one merged live view throughout the main UI.
  // A responsible attorney is a Clio user whose linked contact is not
  // necessarily returned as a matter relationship. Keep the hydrated matter
  // value in the picker source explicitly so selecting "Our Client" can show
  // and persist that contact even when the relationship shortlist is nonempty.
  const responsibleAttorneyContacts = clioMatter?.responsible_attorney ? [clioMatter.responsible_attorney] : [];
  const liveContacts = mergeClioContacts(contacts, accountUsers, matterContacts, responsibleAttorneyContacts);
  const partyFormContacts = resolveMatterContactOptions(
    matterContacts,
    liveContacts,
    [partyForm.contactId, ...partyForm.counselIds, ...partyForm.paralegalIds],
  );
  // Not memoized: this point in the component runs after early returns for
  // loading/error/auth states above, so a hook here would violate the
  // Rules of Hooks (conditionally called between renders).
  const factsById: Record<string, Fact> = {};
  allFacts.forEach(f => { factsById[f.id] = f; });
  const evidenceById: Record<string, Evidence> = {};
  allEvidence.forEach(e => { evidenceById[e.id] = e; });

  const excludedEvidenceIds = new Set<string>();
  ((effectiveProofGraph.motions ?? [])).forEach((m) => {
    ((m.evidence_links ?? [])).forEach((el) => {
      if (el.relationship === 'targets_exclusion' && el.status === 'eliminated_by_court_order') {
        excludedEvidenceIds.add(el.evidence_id);
      }
    });
  });

  // Facts not referenced by any element.fact_links across any claim.
  const linkedFactIds = new Set<string>();
  ((effectiveProofGraph.claims ?? [])).forEach((c) => {
    ((c.elements ?? [])).forEach((el) => {
      ((el.fact_links ?? [])).forEach((l: FactLink) => linkedFactIds.add(l.fact_id));
    });
  });
  const unmappedFacts: Fact[] = allFacts.filter(f => !linkedFactIds.has(f.id));

  const { saveGraph } = graphActions;
  const handleSetAuthorityPrinciples = (target: AuthorityResearchTarget, principleIds: string[]): void => {
    const research = effectiveProofGraph.authority_research ?? { principles: [] };
    const authorityResearch = setPrinciplesForTarget(research, target, principleIds);
    void saveGraph({
      ...effectiveProofGraph,
      authority_research: { ...authorityResearch, is_example: false, updated_at: new Date().toISOString() },
    });
  };
  const {
    handleUpdateFact, handleDeleteFact, handleLinkFact, handleUnlinkFact,
    handleAddAndLinkFact, countFactUsage, handleAddUnmappedFact, handleDeleteUnmappedFact,
  } = graphActions.facts;
  const {
    handleAddOrReuseEvidence, handleUpdateEvidence, handleUnlinkEvidenceFromFact, handleDeleteEvidence,
  } = graphActions.evidence;
  const { handleAddDamage, handleDeleteDamage, handleUpdateDamage } = graphActions.damages;
  const { handleCreateMotion, handleUpdateMotion, handleDeleteMotion } = graphActions.motions;
  const { handleUpdateClaim, handleDeleteClaim, handleUpdateThread } = graphActions.claims;
  const { handleCreateClioTask, handleToggleTaskStatus } = graphActions.tasks;
  const { handleSaveStageLayout, handleUpdateWidgetConfig } = graphActions.templates;
  const { handleUpdateStrategicNotes, handleUpdateCalendarEvents, handleAddCalendarEvent } = graphActions.misc;

  const handleSelectContactFromSearchModal = async (contact: ClioContact): Promise<void> => {
    if (!matterId) return;
    setSearchModalLoading(true);

    let description = "Related Contact";
    const { partyRole, fieldType } = searchModalContext;
    if (fieldType === 'clio_contact_id') {
      description = (nonEmptyString(partyRole) ?? "Client");
    } else if (fieldType === 'counsel_contact_id') {
      if (partyRole === 'Plaintiff') {
        description = "Plaintiff's Counsel";
      } else if (partyRole === 'Defendant') {
        description = "Defense Counsel";
      } else if (partyRole === 'Third Party Plaintiff') {
        description = "Third Party Plaintiff's Counsel";
      } else if (partyRole === 'Third Party Defendant') {
        description = "Third Party Defendant's Counsel";
      } else if (partyRole) {
        description = `${partyRole}'s Counsel`;
      } else {
        description = "Counsel";
      }
    } else if (fieldType === 'paralegal_contact_id') {
      description = partyRole ? `${partyRole}'s Paralegal` : 'Paralegal';
    } else if (fieldType === 'related_contact') {
      description = (nonEmptyString(partyRole) ?? "Related Contact");
    }

    try {
      const res = await apiFetch(`/api/v1/matters/${matterId}/relationships`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contact_id: contact.id,
          description: description,
        }),
      });

      if (!res.ok) {
        logger.error('Failed to create Clio relationship');
      }

      // The search endpoint returns the freshest Clio snapshot. Keep both
      // contact stores in sync so party cards and downstream views can resolve
      // the newly linked id immediately, even over an older startup snapshot.
      setContacts(prev => upsertClioContact(prev, contact));
      setMatterContacts(prev => upsertClioContact(prev, contact));

      if (searchModalOnSelect) {
        searchModalOnSelect(contact);
      }

      notify(`Linked ${contact.name} as ${description}!`);
    } catch (e) {
      logger.error('Error linking contact:', e);
      notify('Failed to link contact in Clio.');
    } finally {
      setSearchModalLoading(false);
      setSearchModalOpen(false);
      setSearchModalOnSelect(null);
    }
  };

  // Dispatch Quick Action form submission
  const handleModalSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      const res = await apiFetch(`/api/v1/matters/${matterId}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action_type: activeModal,
          data: modalData,
        }),
      });
      if (res.ok) {
        // Refresh matters
        const mRes = await apiFetch(`/api/v1/matters/${matterId}`);
        const mData = await mRes.json<MatterResponse>();
        setClioTasks((mData.tasks ?? []));
        setClioActivities((mData.activities ?? []));
        setClioCalendarEvents((mData.calendar_events ?? []));
        setActiveModal(null);
        setModalData({});
      }
    } catch (err) {
      logger.error(err);
    }
  };

  // Commit Court Rules deadlines
  const handleCommitCourtRules = async (): Promise<void> => {
    try {
      await apiFetch(`/api/v1/matters/${matterId}/rules/docket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trigger_id: selectedTrigger,
          events: docketPreview,
        }),
      });
      showAlert('Success', 'Deadlines successfully written to Clio Calendar!');
      setActiveModal(null);
      setDocketPreview([]);
      setSelectedTrigger('');
    } catch (err) {
      showAlert('Error Committing Rules', 'Error committing rules: ' + errorMessage(err));
    }
  };

  const activeThreadClaimIdx = resolved.claims.findIndex((c) => c.id === openThreadClaimId);
  const activeThreadClaim = activeThreadClaimIdx !== -1 ? resolved.claims[activeThreadClaimIdx] : null;

  const contextAssistantUI: ContextAIUIContext = openFactMetadataId && factsById[openFactMetadataId]
    ? { active_view: activeTab, focused_type: 'fact', focused_id: openFactMetadataId, focused_label: (nonEmptyString(factsById[openFactMetadataId].text) ?? `Fact ${openFactMetadataId}`) }
    : activeThreadClaim
      ? { active_view: activeTab, focused_type: 'claim', focused_id: activeThreadClaim.id, focused_label: (nonEmptyString(activeThreadClaim.name) ?? `Claim ${activeThreadClaim.id}`) }
      : activeTab === 'authorities' && authorityFocus
        ? { active_view: activeTab, focused_type: authorityFocus.type, focused_id: authorityFocus.id, focused_label: authorityFocus.label }
        : { active_view: activeTab };

  const refreshContextAssistantGraph = async (): Promise<void> => {
    const response = await apiFetch(`/api/v1/matters/${encodeURIComponent(matterId)}`);
    if (!response.ok) throw new Error('Could not refresh the matter after applying the proposal.');
    const data = await response.json<MatterResponse>();
    setProofGraph(migrateProofGraph(data.graph));
  };

  const openContextReference = (reference: ContextAISourceRef): void => {
    setOpenThreadClaimId(null);
    setOpenFactMetadataId(null);
    setMapperReferenceFocus(null);
    setAuthorityReferenceId(null);
    setDocumentReferenceId(null);
    const navigate = (tab: TabId, action: () => void): void => tryNavigate(tab, action);
    if (reference.type === 'fact') {
      navigate('facts', () => { setFactMetadataInitialSection('basic'); setOpenFactMetadataId(reference.id); });
      return;
    }
    if (reference.type === 'evidence') {
      const linkedFact = allFacts.find(fact => ((fact.evidence_ids ?? [])).includes(reference.id));
      navigate('facts', () => {
        setFactMetadataInitialSection('evidence');
        if (linkedFact) setOpenFactMetadataId(linkedFact.id);
      });
      return;
    }
    if (reference.type === 'claim' || reference.type === 'element') {
      const mapperType: 'claim' | 'element' = reference.type;
      navigate('mapper', () => setMapperReferenceFocus({ type: mapperType, id: reference.id }));
      return;
    }
    if (reference.type === 'authority') {
      navigate('authorities', () => { setAuthorityFocus(null); setAuthorityReferenceId(reference.id); });
      return;
    }
    if (reference.type === 'document') {
      navigate('ai-intake', () => setDocumentReferenceId(reference.id));
    }
  };

  const activeStageID = (nonEmptyString(effectiveProofGraph.active_stage_id) ?? 'presuit');
  const allStages = (resolved.template.stages ?? []);
  const pleadingsIndex = allStages.findIndex((s) => s.id === 'pleadings');
  const activeStageIndex = allStages.findIndex((s) => s.id === activeStageID);
  const hasReachedPleadings = pleadingsIndex !== -1 && activeStageIndex >= pleadingsIndex;

  return (
    <div className="app-container">
      {/* SIDEBAR — see shared/components/layout/AppSidebar.tsx */}
      <AppSidebar
        showWizard={showWizard}
        mattersList={mattersList}
        matterId={matterId}
        setMatterId={setMatterId}
        clioMatter={clioMatter}
        showAllMatters={showAllMatters}
        mattersListLoading={mattersListLoading}
        moreMattersLoading={moreMattersLoading}
        onMatterScopeChange={changeMatterScope}
        activeTab={activeTab}
        hasReachedPleadings={hasReachedPleadings}
        tryNavigate={(tab) => {
          if (tab === 'authorities') setAuthorityFocus(null);
          tryNavigate(tab);
        }}
        setOpenThreadClaimId={setOpenThreadClaimId}
        aiUsage={aiUsage}
      />

      {/* 2. MAIN CANVAS VIEW */}
      <div className="main">
        {/* TOPBAR — see shared/components/layout/AppTopbar.tsx */}
        <AppTopbar
          clioMatter={clioMatter}
          activeStageId={effectiveProofGraph.active_stage_id}
          onOpenUserPrefs={() => setShowUserPrefs(true)}
          matterId={matterId}
          onOpenAIExtract={() => tryNavigate('ai-intake')}
        />

        {/* Scrollable canvas wrapper */}
        <div className="canvas">
          <Suspense fallback={<ViewChunkFallback />}>
          {showWizard ? (
            <OnboardingWizard
              onClose={() => setShowWizard(false)}
              contacts={liveContacts}
              matterContacts={matterContacts}
              onOpenSearchModal={(onSelect, contextInfo) => {
                setSearchModalContext(contextInfo);
                setSearchModalOnSelect(() => onSelect);
                setSearchModalOpen(true);
              }}
              isPremiumUnlocked={isPremiumUnlocked}
              setIsPremiumUnlocked={setIsPremiumUnlocked}
              matterId={matterId}
              matterClientName={(clioMatter?.client ?? '')}
              onInitializeMatter={async (matterPayload) => {
                try {
                  const newId = matterPayload.matter_id;
                  const res = await apiFetch(`/api/v1/matters/${newId}/graph`, jsonInit('PUT', matterPayload));
                  if (res.ok) {
                    setProofGraph(migrateProofGraph(matterPayload));
                    notify('Litigation successfully initialized!');
                    setShowWizard(false);
                    setActiveTab('dashboard');
                    
                    // Sync parent harness if running inside iframe
                    if (window.parent !== window) {
                      window.parent.postMessage({ type: 'MATTER_CREATED', matterId: newId }, '*');
                    }
                    
                    // Switch to the newly created matter
                    setMatterId(newId);
                    
                    // Update URL query parameter
                    const url = new URL(window.location.href);
                    url.searchParams.set('matter_id', newId);
                    window.history.pushState({}, '', url.toString());
                  } else {
                    throw new Error('Failed to save the new litigation on the server.');
                  }
                } catch (err) {
                  logger.error('Error saving new litigation:', err);
                  showAlert('Error', 'Error saving the new litigation.');
                  throw err;
                }
              }}
            />
          ) : (
            <>
          {/* TAB 1: STAGE WORKSPACE BOARD (PLEADINGS) */}
          {activeTab === 'matter-details' && (
            <MatterDetailsPanel
              key={matterId}
              matterId={matterId}
              clioMatter={clioMatter}
              proofGraph={effectiveProofGraph}
              onSave={graphActions.saveGraph}
            />
          )}

          {activeTab === 'dashboard' && (
            <StageBoard
              activeStageId="pleadings"
              widgets={(resolved.stages.find((s) => s.id === 'pleadings')?.widgets ?? [])}
              clioTasks={clioTasks}
              clioDocuments={clioDocuments}
              onOpenModal={(modal) => { setActiveModal(modal); setModalData({}); }}
              onToggleTaskStatus={handleToggleTaskStatus}
              onConfigureBoard={() => undefined}
              stageName={(nonEmptyString(resolved.stages.find((s) => s.id === 'pleadings')?.name) ?? 'Pleadings')}
              stageDesc={(resolved.stages.find((s) => s.id === 'pleadings')?.desc ?? '')}
              onUpdateWidgetConfig={(...args) => { void handleUpdateWidgetConfig(...args); }}
              onSaveLayout={(...args) => { void handleSaveStageLayout(...args); }}
              hasSavedLayout={Boolean(effectiveProofGraph.stage_widgets?.pleadings)}
              matterId={matterId}
              strategicNotes={(effectiveProofGraph.strategic_notes ?? [])}
              onUpdateStrategicNotes={handleUpdateStrategicNotes}
              calendarEvents={clioCalendarEvents}
              onUpdateCalendarEvents={handleUpdateCalendarEvents}
              onAddCalendarEvent={handleAddCalendarEvent}
              clioActivities={clioActivities}
            />
          )}



          {/* TAB 1.75: NEGOTIATION & MEDIATION WORKSPACE */}
          {activeTab === 'negotiation' && (
            <NegotiationPane
              matterId={matterId}
              claims={resolved.claims}
              clioDocuments={clioDocuments}
              allFacts={allFacts}
              authorityResearch={effectiveProofGraph.authority_research}
              onOpenAuthorities={openAuthorities}
            />
          )}

          {activeTab === 'facts' && (
            <div className="view show">
              <FactsLens
                proofGraph={effectiveProofGraph}
                saveGraph={saveGraph}
                matterContacts={matterContacts}
                clioDocuments={clioDocuments}
                preferences={preferences}
                matterId={matterId}
                onSaveCustomFieldPresets={prefsDraft.saveCustomFieldPresets}
                onSaveFactTableTemplates={prefsDraft.saveFactTableTemplates}
              />
            </div>
          )}

          {activeTab === 'case-context' && (
            <CaseContextLens key={matterId} matterId={matterId} proofGraph={effectiveProofGraph} saveGraph={saveGraph} contacts={liveContacts} onOpenReference={openContextReference} onCreateTask={(title, dueDate, description) => handleCreateClioTask('case-intelligence', title, dueDate, description)} />
          )}

          {activeTab === 'authorities' && (
            <AuthoritiesLens
              matterId={matterId}
              proofGraph={effectiveProofGraph}
              saveGraph={saveGraph}
              preferences={preferences}
              focusTarget={authorityFocus}
              focusAuthorityId={authorityReferenceId}
            />
          )}

          {activeTab === 'docket' && (
            <div className="view show">
              <DocketLens matterId={matterId} />
            </div>
          )}

          {/* MAPPER TAB — see shared/components/mapper/MapperTab.tsx (claims,
              split/ladder/side-toggle lenses, unmapped facts pool, and the
              add-claim/add-fact/link-fact modals all live there) */}
          {activeTab === 'experts' && (
            <div className="view show">
              <ExpertsLens
                matterId={matterId}
                contacts={liveContacts}
                clioDocuments={clioDocuments}
                facts={allFacts}
              />
            </div>
          )}

          {activeTab === 'mapper' && (
            <MapperTab
              claims={resolved.claims}
              parties={(effectiveProofGraph.parties ?? [])}
              prefs={resolved.prefs}
              caseParties={(effectiveProofGraph.parties ?? [])}
              clioDocuments={clioDocuments}
              onUpdatePreferences={prefsDraft.updateSavedPreferences}
              factsById={factsById}
              evidenceById={evidenceById}
              allFacts={allFacts}
              excludedEvidenceIds={excludedEvidenceIds}
              onUpdateFact={handleUpdateFact}
              onOpenFactMetadata={(fact) => setOpenFactMetadataId(fact.id)}
              onDeleteFact={handleDeleteFact}
              factUsageCount={countFactUsage}
              onAddOrReuseEvidence={handleAddOrReuseEvidence}
              onUpdateEvidence={handleUpdateEvidence}
              onDeleteEvidence={handleDeleteEvidence}
              onUnlinkEvidenceFromFact={handleUnlinkEvidenceFromFact}
              damages={damages}
              onAddDamage={handleAddDamage}
              onDeleteDamage={handleDeleteDamage}
              onUpdateDamage={handleUpdateDamage}
              onUpdateClaim={handleUpdateClaim}
              onDeleteClaim={handleDeleteClaim}
              onOpenThread={(claimId) => setOpenThreadClaimId(claimId)}
              onLinkFact={handleLinkFact}
              onUnlinkFact={handleUnlinkFact}
              onAddAndLinkFact={handleAddAndLinkFact}
              unmappedFacts={unmappedFacts}
              matterId={matterId}
              jurisdiction={parseJurisdiction(proofGraph)}
              defaultLens={preferences?.default_lens}
              onAddClaim={(newClaim) => void saveGraph({ ...effectiveProofGraph, claims: [...((effectiveProofGraph.claims ?? [])), newClaim] })}
              onAddUnmappedFact={handleAddUnmappedFact}
              onDeleteUnmappedFact={handleDeleteUnmappedFact}
              showPrompt={showPrompt}
              referenceFocus={mapperReferenceFocus}
              authorityResearch={effectiveProofGraph.authority_research}
              onOpenAuthorities={openAuthorities}
              onSetAuthorityPrinciples={handleSetAuthorityPrinciples}
            />
          )}

          {/* TAB 3: MOTIONS HUB */}
          {activeTab === 'motions' && (
            <MotionsHub
              matterId={matterId}
              motions={(effectiveProofGraph.motions ?? [])}
              evidencePool={(effectiveProofGraph.evidence ?? [])}
              preferences={preferences}
              onCreateMotion={handleCreateMotion}
              onUpdateMotion={handleUpdateMotion}
              onDeleteMotion={handleDeleteMotion}
              onCreateEvidence={(ev) => void saveGraph({ ...effectiveProofGraph, evidence: [...((effectiveProofGraph.evidence ?? [])), ev] })}
              facts={allFacts}
              authorityResearch={effectiveProofGraph.authority_research}
              onOpenAuthorities={openAuthorities}
            />
          )}

          {/* PREFERENCES TAB — see shared/components/preferences/PreferencesPanel.tsx */}
          {activeTab === 'preferences' && (
            <PreferencesPanel draft={prefsDraft} contacts={accountUsers} preferences={preferences} />
          )}

          {prefsDraft.showClearDataModal && (
            <ClearDataConfirmModal
              isOpen
              confirmText={prefsDraft.clearDataConfirmText}
              onConfirmTextChange={prefsDraft.setClearDataConfirmText}
              clearing={prefsDraft.clearingData}
              error={prefsDraft.clearDataError}
              phrase={prefsDraft.CLEAR_DATA_CONFIRM_PHRASE}
              onConfirm={() => { void prefsDraft.handleClearAllData(); }}
              onCancel={prefsDraft.closeClearDataModal}
            />
          )}

          {/* PARTIES TAB — see shared/components/parties/PartiesTab.tsx */}
          {activeTab === 'parties' && (
            <PartiesTab
              parties={(effectiveProofGraph.parties ?? [])}
              contacts={liveContacts}
              onAddParty={partyForm.openAdd}
              onEditParty={partyForm.openEdit}
              onDeleteParty={partyForm.deleteParty}
            />
          )}

          {/* TAB: VISUALIZATION */}
          {activeTab === 'visualization' && (
            <div className="view show">
              <ProofGraphVisualizer
                matterId={matterId}
                isPremiumUnlocked={isPremiumUnlocked}
                onUnlockPremium={() => {
                  void launchProCheckout()
                    .then(billing => {
                      if (billing) {
                        setIsPremiumUnlocked(true);
                        notify('Lattice Pro is now active.');
                      }
                    })
                    .catch(error => showAlert('Unable to open checkout', error instanceof Error ? error.message : 'Please try again.'));
                }}
                apiUrl={API_URL}
              />
            </div>
          )}

          {/* TAB: CREATE DOCUMENTS */}
          {activeTab === 'create-documents' && (
            <CreateDocuments
              matterId={matterId}
              proofGraph={effectiveProofGraph}
              contacts={liveContacts}
              accountUsers={accountUsers}
              responsibleAttorney={clioMatter?.responsible_attorney}
              preferences={preferences}
              onUpdatePreferences={(newPrefs) => {
                setPreferences(newPrefs);
                return Promise.resolve();
              }}
            />
          )}

          {/* TAB: REPORTS */}
          {activeTab === 'reports' && (
            <ReportsTab
              matterId={matterId}
              proofGraph={effectiveProofGraph}
              clioTasks={clioTasks}
              contacts={liveContacts}
              preferences={preferences}
              damages={damages}
              calendarEvents={clioCalendarEvents}
              clioActivities={clioActivities}
            />
          )}

          {/* TAB: AI DOCUMENT INTAKE */}
          {activeTab === 'ai-intake' && (
            <AIIntakeHub
              matterId={matterId}
              parties={(effectiveProofGraph.parties ?? [])}
              initialDocumentId={documentReferenceId}
              onSearchContact={(context, onSelect) => {
                setSearchModalContext(context);
                setSearchModalOnSelect(() => onSelect);
                setSearchModalOpen(true);
              }}
            />
          )}
        </>)}
          </Suspense>
        </div>

      </div>

      {/* PARTY ADD/EDIT MODAL — see shared/components/parties/PartyFormModal.tsx */}
      <Suspense fallback={null}>
      {partyForm.isOpen && (
        <PartyFormModal
          form={partyForm}
          matterContacts={partyFormContacts}
          accountUsers={accountUsers}
          onSearchContact={(context, onSelect) => {
            setSearchModalContext(context);
            setSearchModalOnSelect(() => onSelect);
            setSearchModalOpen(true);
          }}
        />
      )}

      {/* 3. SLIDING CONVERSATION THREAD SIDE PANEL */}
      {activeThreadClaim && (
        <ConversationThread
          claim={activeThreadClaim}
          onUpdateThread={(updated) => handleUpdateThread(activeThreadClaimIdx, updated)}
          onCreateClioTask={handleCreateClioTask}
          onClose={() => setOpenThreadClaimId(null)}
        />
      )}

      {openFactMetadataId && factsById[openFactMetadataId] && (
        <FactMetadataPanel
          key={`${openFactMetadataId}:${factMetadataInitialSection}`}
          fact={factsById[openFactMetadataId]}
          proofGraph={effectiveProofGraph}
          saveGraph={saveGraph}
          matterContacts={matterContacts}
          clioDocuments={clioDocuments}
          preferences={preferences}
          matterId={matterId}
          initialSection={factMetadataInitialSection}
          onClose={() => setOpenFactMetadataId(null)}
        />
      )}

      {/* QUICK ACTION / COURT RULES MODAL — see shared/components/QuickActionModal.tsx */}
      {activeModal && (
        <QuickActionModal
          activeModal={activeModal}
          modalData={modalData}
          onModalDataChange={(patch) => setModalData({ ...modalData, ...patch })}
          jurisdictions={jurisdictions}
          selectedJurisdiction={selectedJurisdiction}
          onJurisdictionChange={handleJurisdictionChange}
          ruleTriggers={ruleTriggers}
          selectedTrigger={selectedTrigger}
          onTriggerChange={handleTriggerChange}
          docketPreview={docketPreview}
          onSubmit={(...args) => { void handleModalSubmit(...args); }}
          onCommitCourtRules={() => { void handleCommitCourtRules(); }}
          onCancel={() => { setActiveModal(null); setModalData({}); setDocketPreview([]); }}
        />
      )}

      {!showWizard && (
        <ContextAssistantFAB
          key={matterId}
          matterId={matterId}
          matterLabel={(nonEmptyString(clioMatter?.description) ?? clioMatter?.display_number)}
          uiContext={contextAssistantUI}
          contacts={liveContacts}
          onGraphRefresh={refreshContextAssistantGraph}
          onOpenReference={openContextReference}
        />
      )}

      {/* PREFERENCES SAVE BAR / NAV-GUARD — see shared/components/preferences/PreferencesSaveBar.tsx, shared/components/UnsavedChangesModal.tsx */}
      <PreferencesSaveBar
        visible={activeTab === 'preferences' && isPreferencesDirty}
        onDiscard={prefsDraft.discard}
        onSave={() => { void prefsDraft.save(); }}
      />
      {prefsDraft.pendingTab && (
        <UnsavedChangesModal
          isOpen
          onSaveAndContinue={prefsDraft.confirmSaveAndContinue}
          onDiscardAndContinue={prefsDraft.confirmDiscardAndContinue}
          onCancel={prefsDraft.confirmCancel}
        />
      )}

      {/* GLOBAL USER PREFERENCES OVERLAY — see shared/components/UserPreferencesOverlay.tsx */}
      {showUserPrefs && (
        <UserPreferencesOverlay
          isOpen
          onClose={() => setShowUserPrefs(false)}
          activeSubTab={userPrefsSubTab}
          onSelectSubTab={setUserPrefsSubTab}
          archetype={prefsDraft.archetype}
          onSelectArchetype={prefsDraft.setArchetype}
          defaultLens={prefsDraft.defaultLens}
          onSelectLens={prefsDraft.setDefaultLens}
          onSaveDefaults={() => { void prefsDraft.save(); }}
          preferences={preferences}
          onSaveCustomFieldPresets={prefsDraft.saveCustomFieldPresets}
        />
      )}

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          background: 'var(--navy)',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 1000,
          fontFamily: 'var(--g)',
          fontSize: '13.5px',
          borderLeft: '4px solid var(--primary)'
        }}>
          <span>✨</span> {toastMessage}
        </div>
      )}
      {dbLoading && (
        <DbLoadingGraph states={dbLoadingStates} />
      )}
      <ModalDialog
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        promptFields={modalConfig.promptFields}
        onConfirm={modalConfig.onConfirm}
        onCancel={close}
      />
      {searchModalOpen && (
        <ContactSearchModal
          contacts={liveContacts}
          loading={searchModalLoading}
          contactType={searchModalContext.fieldType === 'counsel_contact_id' || searchModalContext.fieldType === 'paralegal_contact_id'
            ? 'Individual'
            : searchModalContext.partyRole === 'Law Firm' ? 'Company' : undefined}
          onClose={() => {
            setSearchModalOpen(false);
            setSearchModalOnSelect(null);
          }}
          onSelect={(...args) => { void handleSelectContactFromSearchModal(...args); }}
        />
      )}
      </Suspense>
    </div>
  );
}

export default function AppWithDialogs(): React.JSX.Element { return <><App /><ConfirmationHost /></>; }
